import React, { useEffect, useState, useCallback } from "react";
import Navbar from "./Navbar";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { motion } from "framer-motion";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// ─── helpers ──────────────────────────────────────────────────────────────────
const FALLBACK = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
const getPhotos = u =>
  Array.isArray(u.photos) && u.photos.length ? u.photos : [FALLBACK];

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, visible, type = "success" }) {
  return (
    <div className={`lk-toast ${visible ? "lk-toast--show" : ""} lk-toast--${type}`}>
      <span className="lk-toast__icon">{type === "match" ? "💍" : "✓"}</span>
      {message}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="lk-card lk-card--skeleton">
      <div className="lk-skeleton-photo" />
      <div className="lk-card__body">
        <div className="lk-sk-line lk-sk-line--wide" />
        <div className="lk-sk-line lk-sk-line--narrow" />
        <div className="lk-sk-chips">
          {[1,2,3].map(i => <div key={i} className="lk-sk-chip" />)}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="lk-empty">
      <div className="lk-empty__icon">🌸</div>
      <h2 className="lk-empty__title">No likes yet</h2>
      <p className="lk-empty__body">
        Keep your profile complete and active — the right person is still browsing.
      </p>
      <a href="/users" className="lk-btn-primary">Browse profiles</a>
    </div>
  );
}

// ─── Like card ────────────────────────────────────────────────────────────────
function LikeCard({ user, likedBack, onLikeBack, animDelay }) {
  const photos = getPhotos(user);

  return (
    <motion.article
      className="lk-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: animDelay, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      layout
    >
      <div className="lk-card__photo-wrap">
        <Swiper
          modules={[Navigation, Pagination]}
          navigation
          pagination={{ clickable: true }}
          className="lk-swiper"
        >
          {photos.map((url, i) => (
            <SwiperSlide key={i}>
              <img src={url} alt={`${user.name} photo ${i+1}`} className="lk-card__photo" loading="lazy" />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="lk-card__heart-badge" aria-label="Liked you">♥</div>
        <div className="lk-card__age">{user.age}</div>
      </div>

      <div className="lk-card__body">
        <div className="lk-card__top">
          <div>
            <h2 className="lk-card__name">{user.name}</h2>
            <div className="lk-card__meta">
              {user.location   && <span className="lk-meta-item">📍 {user.location}</span>}
              {user.occupation && <span className="lk-meta-item">💼 {user.occupation}</span>}
            </div>
          </div>
        </div>

        {user.personalityTraits?.length > 0 && (
          <div className="lk-card__traits">
            {user.personalityTraits.slice(0, 4).map((t, i) => (
              <span key={i} className="lk-chip">{t}</span>
            ))}
          </div>
        )}

        {user.hobbies?.length > 0 && (
          <div className="lk-card__traits">
            {user.hobbies.slice(0, 3).map((h, i) => (
              <span key={i} className="lk-chip lk-chip--hobby">{h}</span>
            ))}
          </div>
        )}

        <div className="lk-card__cta">
          {likedBack ? (
            <div className="lk-matched-banner">
              <span>💍</span>
              <span>It's a match! Visit <a href="/matches" className="lk-link">Matches</a> to connect.</span>
            </div>
          ) : (
            <button
              className="lk-btn-like"
              onClick={() => onLikeBack(user.id)}
              aria-label={`Like ${user.name} back`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              Like back
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
}

// ─── main ─────────────────────────────────────────────────────────────────────
export default function Likes() {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [likedBack,  setLikedBack]  = useState({});
  const [currentUid, setCurrentUid] = useState(null);
  const [toast,      setToast]      = useState({ visible: false, message: "", type: "success" });

  const auth = getAuth();

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setLoading(false); return; }
      setCurrentUid(user.uid);
      try {
        // who liked the current user
        const likesSnap = await getDocs(
          query(collection(db, "interactions"),
            where("toUserId", "==", user.uid),
            where("type", "==", "like"))
        );
        const likerIds = likesSnap.docs.map(d => d.data().fromUserId);

        // fetch existing matches so we can exclude them
        const matchesSnap = await getDocs(
          query(collection(db, "matches"),
            where("users", "array-contains", user.uid))
        );
        const matchedUserIds = new Set(
          matchesSnap.docs.flatMap(d => d.data().users).filter(id => id !== user.uid)
        );

        // exclude anyone already matched
        const pendingLikerIds = likerIds.filter(id => !matchedUserIds.has(id));

        // fetch their profiles
        const profiles = await Promise.all(
          pendingLikerIds.map(async id => {
            const snap = await getDoc(doc(db, "users", id));
            return snap.exists() ? { id, ...snap.data() } : null;
          })
        );
        setUsers(profiles.filter(Boolean));

        // which ones has the current user already liked back (but not yet matched)
        const myLikesSnap = await getDocs(
          query(collection(db, "interactions"),
            where("fromUserId", "==", user.uid),
            where("type", "==", "like"))
        );
        const myLikedIds = new Set(myLikesSnap.docs.map(d => d.data().toUserId));
        const alreadyLiked = {};
        pendingLikerIds.forEach(id => { if (myLikedIds.has(id)) alreadyLiked[id] = true; });
        setLikedBack(alreadyLiked);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [auth]);

  const handleLikeBack = useCallback(async (userId) => {
    if (!currentUid) return;

    // optimistic UI — flip card to matched state immediately
    setLikedBack(prev => ({ ...prev, [userId]: true }));

    try {
      // 1. Write our like-back interaction
      await setDoc(doc(db, "interactions", `${currentUid}_${userId}`), {
        fromUserId: currentUid,
        toUserId:   userId,
        type:       "like",
        createdAt:  serverTimestamp(),
      });

      // 2. Verify their like still exists (defensive, should always be true here)
      const theirLikeSnap = await getDocs(
        query(
          collection(db, "interactions"),
          where("fromUserId", "==", userId),
          where("toUserId",   "==", currentUid),
          where("type",       "==", "like")
        )
      );

      if (!theirLikeSnap.empty) {
        // 3. ✅ Write the match document.
        //    Sorted-UID key is deterministic — no duplicate matches possible.
        const matchId = [currentUid, userId].sort().join("_");
        await setDoc(
          doc(db, "matches", matchId),
          {
            users:     [currentUid, userId],
            createdAt: serverTimestamp(),
          },
          { merge: true } // idempotent
        );

        // 4. Delete both interaction docs — no longer needed now a match exists
        await Promise.all([
          deleteDoc(doc(db, "interactions", `${currentUid}_${userId}`)),
          deleteDoc(doc(db, "interactions", `${userId}_${currentUid}`)),
        ]);

        // 5. Remove from the likes list — they're now a match
        setUsers(prev => prev.filter(u => u.id !== userId));

        showToast("It's a match! Check your Matches page. 💍", "match");
      } else {
        // Their like was somehow removed — still a valid interaction, just no match
        showToast("Liked! They'll be notified.", "success");
      }
    } catch (err) {
      console.error(err);
      // roll back optimistic update
      setLikedBack(prev => ({ ...prev, [userId]: false }));
      showToast("Something went wrong. Try again.", "error");
    }
  }, [currentUid]);

  const likedBackCount = Object.values(likedBack).filter(Boolean).length;

  return (
    <div className="lk-root">
      <Navbar />
      <Toast message={toast.message} visible={toast.visible} type={toast.type} />

      <div className="lk-header">
        <div className="lk-header__inner">
          <div>
            <h1 className="lk-title">People who liked you</h1>
            <p className="lk-subtitle">
              {loading ? "Loading…" :
               users.length === 0 ? "No likes yet" :
               `${users.length} ${users.length === 1 ? "person has" : "people have"} liked your profile`}
            </p>
          </div>
          {!loading && users.length > 0 && (
            <div className="lk-header__badges">
              <div className="lk-badge lk-badge--likes">
                <span>♥</span>
                <span>{users.length} liked you</span>
              </div>
              {likedBackCount > 0 && (
                <div className="lk-badge lk-badge--matches">
                  <span>💍</span>
                  <span>{likedBackCount} matched</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <main className="lk-container">
        {loading ? (
          <div className="lk-grid">
            {[1,2,3,4].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : users.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="lk-grid">
            {users.map((user, idx) => (
              <LikeCard
                key={user.id}
                user={user}
                likedBack={!!likedBack[user.id]}
                onLikeBack={handleLikeBack}
                animDelay={idx * 0.07}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}